<template>
  <div class="registration-container">
    <div class="registration-wrapper">
      <!-- Left Side - Branding & Info -->
      <div class="registration-left">
        <div class="brand-section">
          <div class="brand-logo mb-4">
            <i class="bi bi-mortarboard-fill"></i>
          </div>
          <h1 class="brand-title mb-3">Join LifeSkill</h1>
          <p class="brand-subtitle mb-5">Start your learning journey today and unlock your potential</p>
          
          <div class="benefits-list">
            <div class="benefit-item">
              <div class="benefit-icon">
                <i class="bi bi-trophy-fill"></i>
              </div>
              <div class="benefit-content">
                <h6>Earn Achievements</h6>
                <p>Unlock badges and rewards as you complete tasks and reach milestones</p>
              </div>
            </div>
            <div class="benefit-item">
              <div class="benefit-icon">
                <i class="bi bi-graph-up-arrow"></i>
              </div>
              <div class="benefit-content">
                <h6>Track Progress</h6>
                <p>Monitor your learning journey with detailed progress reports</p>
              </div>
            </div>
            <div class="benefit-item">
              <div class="benefit-icon">
                <i class="bi bi-people-fill"></i>
              </div>
              <div class="benefit-content">
                <h6>Connect with Family</h6>
                <p>Parents and teachers can support your learning adventure</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Side - Registration Form -->
      <div class="registration-right">
        <div class="registration-form-container">
          <div class="text-center mb-4">
            <h2 class="form-title mb-2">Create Your Account</h2>
            <p class="form-subtitle">Choose your role and get started</p>
          </div>

          <!-- Google Sign-In Section -->
          <div class="google-registration-section mb-4">
            <GoogleSignIn 
              :role="registrationForm.role || 'student'"
              :button-text="`Sign up with Google`"
              :disabled="isLoading"
              @success="handleGoogleSignInSuccess"
              @error="handleGoogleSignInError"
              @loading="handleGoogleLoading"
            />
            
            <div class="divider mt-3">
              <span class="divider-text">or continue with email</span>
            </div>
          </div>

          <!-- Registration Steps -->
          <div class="steps-indicator mb-4">
            <div class="step" :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
              <div class="step-number">1</div>
              <span class="step-label">Role</span>
            </div>
            <div class="step-line" :class="{ completed: currentStep > 1 }"></div>
            <div class="step" :class="{ active: currentStep >= 2, completed: currentStep > 2 }">
              <div class="step-number">2</div>
              <span class="step-label">Details</span>
            </div>
            <div class="step-line" :class="{ completed: currentStep > 2 }"></div>
            <div class="step" :class="{ active: currentStep >= 3 }">
              <div class="step-number">3</div>
              <span class="step-label">Verify</span>
            </div>
          </div>

          <!-- Step 1: Role Selection -->
          <div v-if="currentStep === 1" class="step-content">
            <h5 class="step-title mb-3">Select Your Role</h5>
            <div class="role-selector">
              <div 
                v-for="role in roles" 
                :key="role.value"
                class="role-option"
                :class="{ active: registrationForm.role === role.value }"
                @click="registrationForm.role = role.value"
              >
                <div class="role-icon" :style="{ background: role.color }">
                  <i :class="role.icon"></i>
                </div>
                <div class="role-info">
                  <h6 class="role-title">{{ role.label }}</h6>
                  <small class="role-desc">{{ role.description }}</small>
                  <div class="role-features">
                    <span v-for="feature in role.features" :key="feature" class="feature-tag">
                      {{ feature }}
                    </span>
                  </div>
                </div>
                <div class="role-check">
                  <i class="bi bi-check-circle-fill" v-if="registrationForm.role === role.value"></i>
                  <i class="bi bi-circle" v-else></i>
                </div>
              </div>
            </div>
            
            <button 
              class="btn btn-primary w-100 mt-4"
              :disabled="!registrationForm.role"
              @click="nextStep"
            >
              Continue
              <i class="bi bi-arrow-right ms-2"></i>
            </button>
          </div>

          <!-- Step 2: Personal Details -->
          <div v-if="currentStep === 2" class="step-content">
            <h5 class="step-title mb-3">Personal Information</h5>
            
            <form @submit.prevent="nextStep" class="registration-form">
              <!-- Name Fields -->
              <div class="row mb-3">
                <div class="col-md-6">
                  <label for="firstName" class="form-label">First Name</label>
                  <div class="input-with-icon">
                    <i class="bi bi-person"></i>
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': errors.firstName }"
                      id="firstName"
                      v-model="registrationForm.firstName"
                      placeholder="Enter first name"
                      required
                    />
                    <div v-if="errors.firstName" class="invalid-feedback">{{ errors.firstName }}</div>
                  </div>
                </div>
                <div class="col-md-6">
                  <label for="lastName" class="form-label">Last Name</label>
                  <div class="input-with-icon">
                    <i class="bi bi-person"></i>
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': errors.lastName }"
                      id="lastName"
                      v-model="registrationForm.lastName"
                      placeholder="Enter last name"
                      required
                    />
                    <div v-if="errors.lastName" class="invalid-feedback">{{ errors.lastName }}</div>
                  </div>
                </div>
              </div>

              <!-- Email -->
              <div class="form-group mb-3">
                <label for="email" class="form-label">Email Address</label>
                <div class="input-with-icon">
                  <i class="bi bi-envelope"></i>
                  <input
                    type="email"
                    class="form-control"
                    :class="{ 'is-invalid': errors.email }"
                    id="email"
                    v-model="registrationForm.email"
                    placeholder="Enter your email"
                    @blur="checkEmailExists"
                    required
                  />
                  <div v-if="isCheckingEmail" class="email-checking">
                    <span class="spinner-border spinner-border-sm"></span>
                  </div>
                  <div v-if="!isCheckingEmail && registrationForm.email && isValidEmail(registrationForm.email) && !errors.email" class="email-success">
                    <i class="bi bi-check-circle-fill text-success"></i>
                  </div>
                  <div v-if="errors.email" class="invalid-feedback">{{ errors.email }}</div>
                </div>
              </div>

              <!-- Password -->
              <div class="form-group mb-3">
                <label for="password" class="form-label">Password</label>
                <div class="input-with-icon">
                  <i class="bi bi-lock"></i>
                  <input
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control"
                    :class="{ 'is-invalid': errors.password }"
                    id="password"
                    v-model="registrationForm.password"
                    placeholder="Create a password"
                    required
                  />
                  <button 
                    type="button" 
                    class="password-toggle"
                    @click="showPassword = !showPassword"
                  >
                    <i :class="showPassword ? 'bi-eye-slash' : 'bi-eye'"></i>
                  </button>
                  <div v-if="errors.password" class="invalid-feedback">{{ errors.password }}</div>
                </div>
                <div class="password-strength mt-2">
                  <div class="strength-bar">
                    <div 
                      class="strength-fill" 
                      :class="passwordStrength.class"
                      :style="{ width: passwordStrength.width }"
                    ></div>
                  </div>
                  <small class="strength-text" :class="passwordStrength.class">
                    {{ passwordStrength.text }}
                  </small>
                </div>
              </div>

              <!-- Confirm Password -->
              <div class="form-group mb-3">
                <label for="confirmPassword" class="form-label">Confirm Password</label>
                <div class="input-with-icon">
                  <i class="bi bi-lock-fill"></i>
                  <input
                    type="password"
                    class="form-control"
                    :class="{ 'is-invalid': errors.confirmPassword }"
                    id="confirmPassword"
                    v-model="registrationForm.confirmPassword"
                    placeholder="Confirm your password"
                    required
                  />
                  <div v-if="errors.confirmPassword" class="invalid-feedback">{{ errors.confirmPassword }}</div>
                </div>
              </div>

              <!-- Grade (for students) -->
              <div v-if="registrationForm.role === 'student'" class="form-group mb-3">
                <label for="grade" class="form-label">Grade Level</label>
                <div class="input-with-icon">
                  <i class="bi bi-book"></i>
                  <select
                    class="form-control"
                    :class="{ 'is-invalid': errors.grade }"
                    id="grade"
                    v-model="registrationForm.grade"
                    required
                  >
                    <option value="">Select your grade</option>
                    <option v-for="grade in grades" :key="grade" :value="grade">{{ grade }}</option>
                  </select>
                  <div v-if="errors.grade" class="invalid-feedback">{{ errors.grade }}</div>
                </div>
              </div>

              <!-- Class Code (for students) -->
              <div v-if="registrationForm.role === 'student'" class="form-group mb-4">
                <label for="classCode" class="form-label">Class Code (Optional)</label>
                <div class="input-with-icon">
                  <i class="bi bi-key"></i>
                  <input
                    type="text"
                    class="form-control"
                    id="classCode"
                    v-model="registrationForm.classCode"
                    placeholder="Enter class code from teacher"
                  />
                </div>
                <small class="form-text">Ask your teacher for the class code to join automatically</small>
              </div>

              <!-- Child Email (for parents) -->
              <div v-if="registrationForm.role === 'parent'" class="form-group mb-4">
                <label for="childEmail" class="form-label">Child's Email (Optional)</label>
                <div class="input-with-icon">
                  <i class="bi bi-person-hearts"></i>
                  <input
                    type="email"
                    class="form-control"
                    :class="{ 'is-invalid': errors.childEmail }"
                    id="childEmail"
                    v-model="registrationForm.childEmail"
                    placeholder="Enter your child's email address"
                  />
                  <div v-if="errors.childEmail" class="invalid-feedback">{{ errors.childEmail }}</div>
                </div>
                <small class="form-text">Link your account with your child's account for progress monitoring</small>
              </div>

              <!-- Institution (for teachers) -->
              <div v-if="registrationForm.role === 'teacher'" class="form-group mb-4">
                <label for="institution" class="form-label">School/Institution (Optional)</label>
                <div class="input-with-icon">
                  <i class="bi bi-building"></i>
                  <input
                    type="text"
                    class="form-control"
                    id="institution"
                    v-model="registrationForm.institution"
                    placeholder="Enter your school or institution name"
                  />
                </div>
                <small class="form-text">Help us identify your educational institution</small>
              </div>

              <!-- Terms and Privacy -->
              <div class="form-check mb-4">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="agreeTerms"
                  v-model="registrationForm.agreeTerms"
                  :class="{ 'is-invalid': errors.agreeTerms }"
                  required
                />
                <label class="form-check-label" for="agreeTerms">
                  I agree to the <a href="#" class="link-primary">Terms of Service</a> and 
                  <a href="#" class="link-primary">Privacy Policy</a>
                </label>
                <div v-if="errors.agreeTerms" class="invalid-feedback">{{ errors.agreeTerms }}</div>
              </div>

              <div class="d-flex gap-3">
                <button 
                  type="button"
                  class="btn btn-outline-secondary flex-fill"
                  @click="previousStep"
                >
                  <i class="bi bi-arrow-left me-2"></i>
                  Back
                </button>
                <button 
                  type="submit"
                  class="btn btn-primary flex-fill"
                  :disabled="!isStep2Valid || isLoading"
                >
                  <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
                  {{ isLoading ? 'Creating Account...' : 'Continue' }}
                  <i v-if="!isLoading" class="bi bi-arrow-right ms-2"></i>
                </button>
              </div>
            </form>
          </div>

          <!-- Step 3: Verification & Success -->
          <div v-if="currentStep === 3" class="step-content">
            <div class="text-center">
              <div class="success-icon mb-4">
                <i class="bi bi-check-circle-fill"></i>
              </div>
              <h5 class="step-title mb-3">Account Created Successfully!</h5>
              <p class="success-message mb-4">
                Welcome to LifeSkill, {{ registrationForm.firstName }}! 
                <span v-if="registrationForm.role === 'student'">
                  Get ready for an amazing learning adventure!
                  <span v-if="registrationForm.classCode">
                    <br><small class="text-muted">We'll connect you with your teacher's class automatically.</small>
                  </span>
                </span>
                <span v-else-if="registrationForm.role === 'parent'">
                  You can now monitor your child's progress and support their learning journey.
                  <span v-if="registrationForm.childEmail">
                    <br><small class="text-muted">We'll link your account with your child's account for progress monitoring.</small>
                  </span>
                </span>
                <span v-else>
                  You can now create classes and help students achieve their goals.
                  <span v-if="registrationForm.institution">
                    <br><small class="text-muted">Your institution has been recorded for identification purposes.</small>
                  </span>
                </span>
              </p>

              <div class="account-summary mb-4">
                <div class="summary-item">
                  <strong>Role:</strong> {{ selectedRoleData.label }}
                </div>
                <div class="summary-item">
                  <strong>Email:</strong> {{ registrationForm.email }}
                </div>
                <div v-if="registrationForm.grade" class="summary-item">
                  <strong>Grade:</strong> {{ registrationForm.grade }}
                </div>
                <div v-if="registrationForm.classCode" class="summary-item">
                  <strong>Class Code:</strong> {{ registrationForm.classCode }}
                </div>
                <div v-if="registrationForm.childEmail" class="summary-item">
                  <strong>Child's Email:</strong> {{ registrationForm.childEmail }}
                </div>
                <div v-if="registrationForm.institution" class="summary-item">
                  <strong>Institution:</strong> {{ registrationForm.institution }}
                </div>
              </div>

              <button 
                class="btn btn-primary btn-lg"
                @click="completeRegistration"
              >
                <i class="bi bi-box-arrow-in-right me-2"></i>
                Sign In to Your Account
              </button>

              <p class="mt-3">
                <small class="text-muted">
                  A verification email has been sent to {{ registrationForm.email }}
                </small>
              </p>
            </div>
          </div>

          <!-- Already have account link -->
          <div class="text-center mt-4">
            <p class="mb-0">
              Already have an account? 
              <a href="#" class="link-primary fw-semibold" @click.prevent="goToLogin">
                Sign In
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { validatePasswordStrength } from '../services/validationService.js'
import GoogleSignIn from './GoogleSignIn.vue'

export default {
  name: 'RegistrationPage',
  
  components: {
    GoogleSignIn
  },
  
  emits: ['registration-complete', 'switch-to-login'],
  
  data() {
    return {
      currentStep: 1,
      showPassword: false,
      errors: {},
      createdUser: null,
      isLoading: false,
      isCheckingEmail: false,
              registrationForm: {
        role: '',
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        grade: '',
        classCode: '',
        childEmail: '',
        institution: '',
        agreeTerms: false
      },
      roles: [
        {
          value: 'student',
          label: 'Student',
          icon: 'bi-person-fill',
          color: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
          description: 'Learn, explore, and grow your skills',
          features: ['Interactive Learning', 'Progress Tracking', 'Rewards & Badges']
        },
        {
          value: 'parent',
          label: 'Parent',
          icon: 'bi-people-fill',
          color: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
          description: 'Support your child\'s learning journey',
          features: ['Progress Monitoring', 'Communication', 'Reward Management']
        },
        {
          value: 'teacher',
          label: 'Teacher',
          icon: 'bi-mortarboard-fill',
          color: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
          description: 'Guide and inspire young minds',
          features: ['Class Management', 'Student Analytics', 'Content Creation']
        }
      ],
      grades: [
        'Kindergarten', '1st Grade', '2nd Grade', '3rd Grade', '4th Grade', 
        '5th Grade', '6th Grade', '7th Grade', '8th Grade'
      ]
    }
  },
  
  computed: {
    selectedRoleData() {
      return this.roles.find(role => role.value === this.registrationForm.role) || {}
    },
    
    passwordStrength() {
      const password = this.registrationForm.password
      if (!password) return { class: '', width: '0%', text: '' }
      
      const strength = validatePasswordStrength(password)
      
      const widthMap = {
        weak: '33%',
        medium: '66%',
        strong: '100%'
      }
      
      const textMap = {
        weak: `Weak - Add ${strength.feedback.slice(0, 2).join(', ')}`,
        medium: 'Medium - Add more complexity',
        strong: 'Strong password!'
      }
      
      return {
        class: strength.strength,
        width: widthMap[strength.strength],
        text: textMap[strength.strength]
      }
    },
    
    isStep2Valid() {
      return this.registrationForm.firstName && 
             this.registrationForm.lastName && 
             this.registrationForm.email && 
             this.registrationForm.password && 
             this.registrationForm.confirmPassword && 
             this.registrationForm.agreeTerms &&
             (this.registrationForm.role !== 'student' || this.registrationForm.grade)
    }
  },
  
  methods: {
    nextStep() {
      if (this.currentStep === 1 && this.registrationForm.role) {
        this.currentStep = 2
      } else if (this.currentStep === 2 && this.validateStep2()) {
        this.currentStep = 3
        this.createAccount()
      }
    },
    
    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--
        this.errors = {}
      }
    },
    
    validateStep2() {
      this.errors = {}
      let isValid = true
      
      if (!this.registrationForm.firstName.trim()) {
        this.errors.firstName = 'First name is required'
        isValid = false
      }
      
      if (!this.registrationForm.lastName.trim()) {
        this.errors.lastName = 'Last name is required'
        isValid = false
      }
      
      if (!this.registrationForm.email.trim()) {
        this.errors.email = 'Email is required'
        isValid = false
      } else if (!this.isValidEmail(this.registrationForm.email)) {
        this.errors.email = 'Please enter a valid email address'
        isValid = false
      }
      
      if (!this.registrationForm.password) {
        this.errors.password = 'Password is required'
        isValid = false
      } else {
        const strength = validatePasswordStrength(this.registrationForm.password)
        if (!strength.isValid) {
          this.errors.password = 'Password must be at least medium strength'
          isValid = false
        }
      }
      
      if (this.registrationForm.password !== this.registrationForm.confirmPassword) {
        this.errors.confirmPassword = 'Passwords do not match'
        isValid = false
      }
      
      if (this.registrationForm.role === 'student' && !this.registrationForm.grade) {
        this.errors.grade = 'Grade level is required for students'
        isValid = false
      }
      
      if (this.registrationForm.role === 'parent' && this.registrationForm.childEmail) {
        if (!this.isValidEmail(this.registrationForm.childEmail)) {
          this.errors.childEmail = 'Please enter a valid email address for your child'
          isValid = false
        } else if (this.registrationForm.childEmail === this.registrationForm.email) {
          this.errors.childEmail = 'Child email cannot be the same as your email'
          isValid = false
        }
      }
      
      if (!this.registrationForm.agreeTerms) {
        this.errors.agreeTerms = 'You must agree to the terms and privacy policy'
        isValid = false
      }
      
      return isValid
    },
    
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },
    
    async createAccount() {
      this.isLoading = true
      try {
        // Call the registerUser function from authService.js
        const { registerUser } = await import('../services/authService.js')
        const newUser = await registerUser({
          firstName: this.registrationForm.firstName,
          lastName: this.registrationForm.lastName,
          email: this.registrationForm.email,
          password: this.registrationForm.password,
          role: this.registrationForm.role,
          grade: this.registrationForm.grade,
          classCode: this.registrationForm.classCode,
          childEmail: this.registrationForm.childEmail,
          institution: this.registrationForm.institution
        })
        
        // Store the user data for the success step
        this.createdUser = newUser
        
      } catch (error) {
        console.error('Registration failed:', error)
        this.currentStep = 2
        this.errors.general = error.message || 'Registration failed. Please try again.'
      } finally {
        this.isLoading = false
      }
    },
    
    completeRegistration() {
      // Navigate to login page after successful registration
      this.$router.push('/')
    },
    
    goToLogin() {
      // Navigate to login page
      this.$router.push('/')
    },
    
    async checkEmailExists() {
      if (this.registrationForm.email && this.isValidEmail(this.registrationForm.email)) {
        this.isCheckingEmail = true
        try {
          const { authService } = await import('../services/authService.js')
          const isAvailable = await authService.checkEmailAvailability(this.registrationForm.email)
          if (!isAvailable) {
            this.errors.email = 'This email is already registered'
          } else if (this.errors.email === 'This email is already registered') {
            delete this.errors.email
          }
        } catch (error) {
          console.error('Error checking email availability:', error)
        } finally {
          this.isCheckingEmail = false
        }
      }
    },

    // Google Sign-In event handlers
    handleGoogleSignInSuccess(result) {
      // Handle successful Google sign-in/registration
      this.successMessage = `Welcome, ${result.user.name}!`
      
      // Set the current step to verification if it's a new user
      if (result.is_new_user) {
        this.currentStep = 3
        this.createdUser = result.user
      } else {
        // Existing user, redirect to dashboard
        localStorage.setItem('currentUser', JSON.stringify(result.user))
        
        setTimeout(() => {
          // Redirect to appropriate dashboard
          switch (result.user.role) {
            case 'student':
              this.$router.push('/student-dashboard')
              break
            case 'parent':
              this.$router.push('/parent-dashboard')
              break
            case 'teacher':
              this.$router.push('/teacher-dashboard')
              break
            default:
              this.$router.push('/dashboard')
              break
          }
        }, 1000)
      }
    },

    handleGoogleSignInError(error) {
      // Handle Google sign-in error
      this.errorMessage = error.message || 'Google sign-in failed. Please try again.'
    },

    handleGoogleLoading(isLoading) {
      // Handle loading state from Google Sign-In
      this.isLoading = isLoading
    }
  }
}
</script>

<style scoped>
/* Container & Layout */
.registration-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 0;
}

.registration-wrapper {
  width: 100%;
  max-width: 1200px;
  background: white;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  display: flex;
  min-height: 80vh;
}

/* Left Side - Branding */
.registration-left {
  flex: 1;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  color: white;
}

.registration-left::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat;
  pointer-events: none;
}

.brand-section {
  position: relative;
  z-index: 1;
}

.brand-logo {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  backdrop-filter: blur(10px);
}

.brand-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: white;
  margin: 0;
}

.brand-subtitle {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
}

.benefits-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.benefit-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.benefit-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #fbbf24;
  flex-shrink: 0;
}

.benefit-content h6 {
  color: white;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
}

.benefit-content p {
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
}

/* Right Side - Form */
.registration-right {
  flex: 1;
  padding: 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow-y: auto;
}

.registration-form-container {
  max-width: 500px;
  margin: 0 auto;
  width: 100%;
}

.form-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.form-subtitle {
  color: #6b7280;
  font-size: 1rem;
}

/* Steps Indicator */
.steps-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 2rem 0;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.step-number {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e5e7eb;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.step.active .step-number {
  background: #3b82f6;
  color: white;
}

.step.completed .step-number {
  background: #10b981;
  color: white;
}

.step-label {
  font-size: 0.8rem;
  color: #6b7280;
  font-weight: 500;
}

.step.active .step-label {
  color: #3b82f6;
}

.step.completed .step-label {
  color: #10b981;
}

.step-line {
  width: 60px;
  height: 2px;
  background: #e5e7eb;
  margin: 0 1rem;
  transition: all 0.3s ease;
}

.step-line.completed {
  background: #10b981;
}

/* Step Content */
.step-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.step-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  text-align: center;
}

/* Role Selection */
.role-selector {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.role-option {
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f9fafb;
}

.role-option:hover {
  border-color: #d1d5db;
  background: #f3f4f6;
  transform: translateY(-2px);
}

.role-option.active {
  border-color: #3b82f6;
  background: #eff6ff;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.role-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  margin-right: 1rem;
  flex-shrink: 0;
}

.role-info {
  flex: 1;
}

.role-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.25rem 0;
}

.role-desc {
  color: #6b7280;
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
}

.role-features {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.feature-tag {
  background: #e0e7ff;
  color: #3730a3;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 500;
}

.role-check {
  color: #3b82f6;
  font-size: 1.5rem;
}

/* Form Styling */
.form-group {
  margin-bottom: 1rem;
}

.form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.input-with-icon {
  position: relative;
}

.input-with-icon i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 1rem;
  z-index: 2;
}

.input-with-icon .form-control {
  padding-left: 2.75rem;
  height: 48px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
}

.input-with-icon .form-control:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.password-toggle {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  z-index: 3;
}

.password-toggle:hover {
  color: #6b7280;
}

.email-checking {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
}

.email-success {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
}

/* Password Strength */
.password-strength {
  margin-top: 0.5rem;
}

.strength-bar {
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
}

.strength-fill {
  height: 100%;
  border-radius: 2px;
  transition: all 0.3s ease;
}

.strength-fill.weak {
  background: #ef4444;
}

.strength-fill.medium {
  background: #f59e0b;
}

.strength-fill.strong {
  background: #10b981;
}

.strength-text {
  font-size: 0.8rem;
  font-weight: 500;
}

.strength-text.weak {
  color: #ef4444;
}

.strength-text.medium {
  color: #f59e0b;
}

.strength-text.strong {
  color: #10b981;
}

/* Form Check */
.form-check-input {
  width: 1.2rem;
  height: 1.2rem;
  margin-top: 0.125rem;
  border: 2px solid #d1d5db;
  border-radius: 4px;
}

.form-check-input:checked {
  background-color: #3b82f6;
  border-color: #3b82f6;
}

.form-check-label {
  font-size: 0.9rem;
  color: #374151;
  margin-left: 0.5rem;
}

/* Buttons */
.btn {
  height: 48px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border: none;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(59, 130, 246, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-outline-secondary {
  border: 2px solid #e5e7eb;
  color: #6b7280;
  background: white;
}

.btn-outline-secondary:hover {
  border-color: #d1d5db;
  background: #f9fafb;
}

/* Success Step */
.success-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  font-size: 2.5rem;
  color: white;
}

.success-message {
  color: #6b7280;
  font-size: 1rem;
  line-height: 1.6;
}

.account-summary {
  background: #f9fafb;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: left;
}

.summary-item {
  margin-bottom: 0.5rem;
  color: #374151;
}

.summary-item:last-child {
  margin-bottom: 0;
}

/* Form Text */
.form-text {
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

/* Link */
.link-primary {
  color: #3b82f6;
  text-decoration: none;
}

.link-primary:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

/* Responsive Design */
@media (max-width: 768px) {
  .registration-wrapper {
    flex-direction: column;
    margin: 1rem;
    border-radius: 16px;
  }
  
  .registration-left {
    padding: 2rem;
    text-align: center;
  }
  
  .registration-right {
    padding: 2rem;
  }
  
  .benefits-list {
    gap: 1rem;
  }
  
  .benefit-item {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }
  
  .steps-indicator {
    margin: 1rem 0;
  }
  
  .step-line {
    width: 40px;
    margin: 0 0.5rem;
  }
  
  .role-option {
    padding: 1rem;
  }
  
  .role-features {
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .registration-container {
    padding: 1rem 0;
  }
  
  .registration-left,
  .registration-right {
    padding: 1.5rem;
  }
  
  .brand-title {
    font-size: 2rem;
  }
  
  .form-title {
    font-size: 1.5rem;
  }
  
  .role-option {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
  
  .role-info {
    text-align: center;
  }
}
</style> 